import React, { useState, useEffect } from 'react';
import { ChevronDown, Loader2, Pill } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { TabSkeleton } from '../../../skeletons';
import Modal from '../../../components/Modal';
import residencesService from '../../../services/carehome_manager/residencesService';

const NutrientsTab = () => {
    const { id } = useParams();
    const [nutrientsType, setNutrientsType] = useState('Food'); // 'Fluid' or 'Food'
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({
        foodRecords: [],
        fluidRecords: [],
        foodChartData: [],
        fluidChartData: [],
        // Totals come from the API so the tab never re-adds the numbers itself
        totals: { days: 7, calories: 0, fluidMl: 0, todayCalories: 0, todayFluidMl: 0 }
    });

    // Goal editor. `kind` is 'calories' | 'fluid' — one dialog for both, so the
    // wording and validation cannot drift apart.
    const [goalEditor, setGoalEditor] = useState(null);
    const [goalValue, setGoalValue] = useState('');
    const [goalError, setGoalError] = useState(null);
    const [savingGoal, setSavingGoal] = useState(false);

    useEffect(() => {
        const fetchNutrientData = async () => {
            try {
                setLoading(true);
                const response = await residencesService.getNutrientData(id);
                setData({
                    foodRecords: [],
                    fluidRecords: [],
                    foodChartData: [],
                    fluidChartData: [],
                    totals: { days: 7, calories: 0, fluidMl: 0, todayCalories: 0, todayFluidMl: 0 },
                    ...response.data,
                });
            } catch (err) {
                console.error('Error fetching nutrient data:', err);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchNutrientData();
        }
    }, [id]);

    /**
     * Y-axis top for a chart. It used to be pinned at 1600ml / 2000kcal, so a
     * real day of 20ml drew a flat line along the bottom and looked broken.
     * Scale to the data with headroom, rounded up to a readable step, and only
     * fall back to the full target when there is nothing logged.
     */
    const chartMax = (chartData, emptyFallback) => {
        const peak = Math.max(0, ...(chartData || []).map(d => d.value || 0));
        if (peak === 0) return emptyFallback;
        const withHeadroom = peak * 1.25;
        const step = withHeadroom <= 100 ? 20 : withHeadroom <= 500 ? 50 : withHeadroom <= 2000 ? 200 : 500;
        return Math.ceil(withHeadroom / step) * step;
    };

    const generateChartPath = (chartData, maxValue) => {
        if (!chartData || chartData.length === 0) return "";
        const width = 1000;
        const height = 200;
        const stepX = width / (chartData.length - 1);
        
        const points = chartData.map((d, i) => {
            const x = i * stepX;
            const y = height - (maxValue > 0 ? (d.value / maxValue) * height : 0);
            return `${x},${y}`;
        });

        return `M ${points[0]} ${points.map((p, i) => i === 0 ? '' : `L ${p}`).join(' ')}`;
    };

    const generateAreaPath = (chartPath) => {
        if (!chartPath) return "";
        return `${chartPath} L 1000,200 L 0,200 Z`;
    };

    const FluidChart = () => {
        const maxValue = chartMax(data.fluidChartData, 1600);
        const path = generateChartPath(data.fluidChartData, maxValue);
        const areaPath = generateAreaPath(path);

        return (
            <div className="w-full mt-6">
                <div className="flex items-center justify-between mb-4 px-2">
                    <h3 className="text-[16px] font-semibold text-gray-800 dark:text-gray-100 italic">Fluid chart (ml)</h3>
                    <div className="flex gap-2">
                        <button className="flex items-center gap-2 px-3 py-1.5 border border-gray-100 dark:border-gray-800 rounded-md text-[11px] font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f] transition">
                            Last 7 Days
                        </button>
                    </div>
                </div>

                <div className="flex">
                    <div className="w-[40px] flex flex-col justify-between text-[11px] font-medium text-gray-400 dark:text-gray-500 pb-[30px] pr-2 text-right">
                        <span>{maxValue}</span>
                        <span>{Math.round(maxValue * 0.75)}</span>
                        <span>{Math.round(maxValue * 0.5)}</span>
                        <span>{Math.round(maxValue * 0.25)}</span>
                        <span>0</span>
                    </div>

                    <div className="flex-1 relative h-[180px]">
                        <svg viewBox="0 0 1000 200" className="w-full h-full" preserveAspectRatio="none">
                            <defs>
                                <linearGradient id="gradGreen" x1="0%" y1="0%" x2="0%" y2="100%">
                                    <stop offset="0%" stopColor="#22c55e" stopOpacity="0.4" />
                                    <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
                                </linearGradient>
                            </defs>
                            <line x1="0" y1="0" x2="1000" y2="0" stroke="#f1f5f9" strokeWidth="1" className="dark:stroke-gray-800" />
                            <line x1="0" y1="66.6" x2="1000" y2="66.6" stroke="#f1f5f9" strokeWidth="1" className="dark:stroke-gray-800" />
                            <line x1="0" y1="133.3" x2="1000" y2="133.3" stroke="#f1f5f9" strokeWidth="1" className="dark:stroke-gray-800" />
                            <line x1="0" y1="200" x2="1000" y2="200" stroke="#f1f5f9" strokeWidth="1" className="dark:stroke-gray-800" />

                            <path d={areaPath} fill="url(#gradGreen)" />
                            <path d={path} fill="none" stroke="#22c55e" strokeWidth="3" />
                            
                            {data.fluidChartData.map((d, i) => (
                                <circle 
                                    key={i} 
                                    cx={i * (1000 / (data.fluidChartData.length - 1))} 
                                    cy={200 - (maxValue > 0 ? (d.value / maxValue) * 200 : 0)} 
                                    r="4" 
                                    fill="#22c55e" 
                                    stroke="white" 
                                    strokeWidth="2" 
                                />
                            ))}
                        </svg>
                        <div className="absolute -bottom-6 w-full flex justify-between text-[11px] font-medium text-gray-500 dark:text-gray-400">
                            {data.fluidChartData.map(d => (
                                <span key={d.date}>{new Date(d.date).toLocaleDateString('en-GB', { weekday: 'short' })}</span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const FoodChart = () => {
        const maxValue = chartMax(data.foodChartData, 2000);
        const path = generateChartPath(data.foodChartData, maxValue);
        const areaPath = generateAreaPath(path);

        return (
            <div className="w-full mt-6">
                <div className="flex items-center justify-between mb-4 px-2">
                    <h3 className="text-[16px] font-semibold text-gray-800 dark:text-gray-100 italic">Calories chart (kcal)</h3>
                    <div className="flex gap-2">
                        <button className="flex items-center gap-2 px-3 py-1.5 border border-gray-100 dark:border-gray-800 rounded-md text-[11px] font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f] transition">
                            Last 7 Days
                        </button>
                    </div>
                </div>

                <div className="flex">
                    <div className="w-[40px] flex flex-col justify-between text-[11px] font-medium text-gray-400 dark:text-gray-500 pb-[30px] pr-2 text-right">
                        <span>{maxValue}</span>
                        <span>{Math.round(maxValue * 0.75)}</span>
                        <span>{Math.round(maxValue * 0.5)}</span>
                        <span>{Math.round(maxValue * 0.25)}</span>
                        <span>0</span>
                    </div>

                    <div className="flex-1 relative h-[180px]">
                        <svg viewBox="0 0 1000 200" className="w-full h-full" preserveAspectRatio="none">
                            <defs>
                                <linearGradient id="gradBlue" x1="0%" y1="0%" x2="0%" y2="100%">
                                    <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.5" />
                                    <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                                </linearGradient>
                            </defs>
                            <line x1="0" y1="0" x2="1000" y2="0" stroke="#f1f5f9" strokeWidth="1" className="dark:stroke-gray-800" />
                            <line x1="0" y1="66.6" x2="1000" y2="66.6" stroke="#f1f5f9" strokeWidth="1" className="dark:stroke-gray-800" />
                            <line x1="0" y1="133.3" x2="1000" y2="133.3" stroke="#f1f5f9" strokeWidth="1" className="dark:stroke-gray-800" />
                            <line x1="0" y1="200" x2="1000" y2="200" stroke="#f1f5f9" strokeWidth="1" className="dark:stroke-gray-800" />

                            <path d={areaPath} fill="url(#gradBlue)" />
                            <path d={path} fill="none" stroke="#3b82f6" strokeWidth="4" />
                            
                            {data.foodChartData.map((d, i) => (
                                <circle 
                                    key={i} 
                                    cx={i * (1000 / (data.foodChartData.length - 1))} 
                                    cy={200 - (maxValue > 0 ? (d.value / maxValue) * 200 : 0)} 
                                    r="4" 
                                    fill="#3b82f6" 
                                    stroke="white" 
                                    strokeWidth="2" 
                                />
                            ))}
                        </svg>
                        <div className="absolute -bottom-6 w-full flex justify-between text-[11px] font-medium text-gray-500 dark:text-gray-400">
                            {data.foodChartData.map(d => (
                                <span key={d.date}>{new Date(d.date).toLocaleDateString('en-GB', { weekday: 'short' })}</span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return <TabSkeleton />;
    }

    const currentRecords = nutrientsType === 'Fluid' ? data.fluidRecords : data.foodRecords;

    // Prefer the API's totals; derive them from the charts when talking to an
    // API build that does not send them yet, so the tiles are never blank while
    // records are clearly there.
    // The ml on a fluid row: the number when the API sends one, otherwise read
    // back out of the "250ml" string an older API build returns.
    const mlOf = (row) => {
        if (typeof row.amountMl === 'number') return row.amountMl;
        const parsed = parseFloat(String(row.in || '').replace(/[^\d.]/g, ''));
        return Number.isFinite(parsed) ? parsed : 0;
    };

    const sum = (rows) => (rows || []).reduce((t, r) => t + (r.value || 0), 0);
    const lastOf = (rows) => (rows && rows.length ? rows[rows.length - 1].value || 0 : 0);
    const totals = data.totals || {
        days: (data.fluidChartData || []).length || 7,
        calories: sum(data.foodChartData),
        fluidMl: sum(data.fluidChartData),
        todayCalories: lastOf(data.foodChartData),
        todayFluidMl: lastOf(data.fluidChartData),
    };

    // Macro grams. The API sends them; when it doesn't (older build), add up the
    // per-record values so the card still reflects the records on screen.
    const sumField = (rows, field) =>
        (rows || []).reduce((t, r) => t + (Number(r[field]) || 0), 0);
    const macros = totals.macros || {
        carbs: sumField(data.foodRecords, 'carbs') + sumField(data.fluidRecords, 'carbs'),
        fat: sumField(data.foodRecords, 'fat') + sumField(data.fluidRecords, 'fat'),
        protein: sumField(data.foodRecords, 'protein') + sumField(data.fluidRecords, 'protein'),
    };
    const macroGoals = data.goals?.macros || { carbs: 250, fat: 67, protein: 100 };
    // Targets are per day, so the bars track today against them — the window
    // total is shown as text beside it.
    const todayMacros = totals.todayMacros || null;

    const goals = data.goals || { calories: 2000, fluidMl: 2000 };

    const GOAL_KINDS = {
        calories: { label: 'calorie', unit: 'kcal', max: 8000, current: goals.calories, presets: [1600, 1800, 2000, 2200] },
        fluid: { label: 'fluid', unit: 'ml', max: 10000, current: goals.fluidMl, presets: [1500, 1800, 2000, 2500] },
    };

    const openGoalEditor = (kind) => {
        setGoalEditor(kind);
        setGoalValue(String(GOAL_KINDS[kind].current ?? ''));
        setGoalError(null);
    };

    const saveGoal = async () => {
        const kind = GOAL_KINDS[goalEditor];
        const entered = parseInt(goalValue, 10);
        if (!Number.isFinite(entered) || entered <= 0 || entered > kind.max) {
            setGoalError(`Enter a goal between 1 and ${kind.max} ${kind.unit}`);
            return;
        }

        try {
            setSavingGoal(true);
            const response = await residencesService.updateNutritionGoals(id, {
                [goalEditor === 'fluid' ? 'fluidMl' : 'calories']: entered,
            });
            // Take the saved goals straight back — the macro targets are derived
            // from the calorie goal server-side, so they change with it.
            setData((prev) => ({ ...prev, goals: response.data || prev.goals }));
            setGoalEditor(null);
        } catch (err) {
            setGoalError(err?.response?.data?.message || 'Could not save the goal. Please try again.');
        } finally {
            setSavingGoal(false);
        }
    };

    const MACRO_ROWS = [
        { key: 'carbs', label: 'Carb', color: '#7C3AED', track: 'rgba(124, 58, 237, 0.15)' },
        { key: 'fat', label: 'Fat', color: '#EF4444', track: 'rgba(239, 68, 68, 0.15)' },
        { key: 'protein', label: 'Protein', color: '#F59E0B', track: 'rgba(245, 158, 11, 0.15)' },
    ];
    const hasMacros = MACRO_ROWS.some((row) => (macros[row.key] || 0) > 0);

    return (
        <div className="mt-6 animate-in fade-in duration-300">
            {/* Header / Sub-tabs */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-1 bg-[#F5F9FF] dark:bg-[#262b33] p-1 rounded-lg border border-[#A5C0F3] dark:border-gray-800">
                    <button
                        onClick={() => setNutrientsType('Food')}
                        className={`px-6 py-1.5 rounded-md text-[13px] font-semibold transition ${nutrientsType === 'Food' ? 'bg-[#2563EB] text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}
                    >
                        Food
                    </button>
                    <button
                        onClick={() => setNutrientsType('Fluid')}
                        className={`px-6 py-1.5 rounded-md text-[13px] font-semibold transition ${nutrientsType === 'Fluid' ? 'bg-[#2563EB] text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}
                    >
                        Fluid
                    </button>
                </div>

                <div className="flex gap-3">
                    <button
                        onClick={() => openGoalEditor(nutrientsType === 'Fluid' ? 'fluid' : 'calories')}
                        className="bg-[#2563EB] hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition shadow-sm"
                    >
                        Edit {nutrientsType === 'Fluid' ? 'fluid' : 'calorie'} goal
                    </button>
                </div>
            </div>

            {/* Monitoring Title */}
            <h2 className="text-[20px] font-bold text-gray-800 dark:text-gray-100 mb-6">
                {nutrientsType} Monitoring Record
            </h2>

            {/* Totals — today and across the charted window. Calories are the
                estimate made when the care note was submitted; fluid is the ml
                the carer recorded. */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                {[
                    { label: 'Calories today', value: `${totals.todayCalories} kcal`, hint: `Goal ${goals.calories} kcal` },
                    { label: `Calories (last ${totals.days} days)`, value: `${totals.calories} kcal` },
                    { label: 'Fluid today', value: `${totals.todayFluidMl} ml`, hint: `Goal ${goals.fluidMl} ml` },
                    { label: `Fluid (last ${totals.days} days)`, value: `${totals.fluidMl} ml` },
                ].map((tile) => (
                    <div
                        key={tile.label}
                        className="bg-white dark:bg-[#262b33] border border-gray-50 dark:border-gray-800 rounded-[16px] shadow-sm px-5 py-4"
                    >
                        <div className="text-[11px] font-bold text-[#6B7280] dark:text-gray-400 uppercase tracking-wider">
                            {tile.label}
                        </div>
                        <div className="mt-1 text-[18px] font-bold text-gray-800 dark:text-gray-100">
                            {tile.value}
                        </div>
                        {tile.hint && (
                            <div className="mt-0.5 text-[11px] font-medium text-gray-400 dark:text-gray-500">
                                {tile.hint}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Nutrition info — carbs / fat / protein in grams, the same
                estimate the mobile app shows. Bars track TODAY against the
                daily target; the window total is stated next to it so the two
                figures are never confused. */}
            <div className="bg-white dark:bg-[#262b33] border border-gray-50 dark:border-gray-800 rounded-[24px] shadow-sm px-8 py-6 mb-8">
                <div className="flex items-center justify-between mb-5">
                    <h3 className="text-[16px] font-semibold text-gray-800 dark:text-gray-100 italic">Nutrition info</h3>
                    <span className="text-[11px] font-bold text-[#6B7280] dark:text-gray-400 uppercase tracking-wider">
                        Today vs daily target
                    </span>
                </div>

                {hasMacros ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-5">
                        {MACRO_ROWS.map((row) => {
                            const today = todayMacros ? (todayMacros[row.key] || 0) : (macros[row.key] || 0);
                            const goal = macroGoals[row.key] || 1;
                            const pct = Math.min(100, (today / goal) * 100);

                            return (
                                <div key={row.key}>
                                    <div className="flex items-baseline justify-between mb-2">
                                        <span className="text-[13px] font-semibold" style={{ color: row.color }}>
                                            {row.label}
                                        </span>
                                        <span className="text-[14px] font-bold text-gray-800 dark:text-gray-100">
                                            {today.toFixed(1)} g
                                            <span className="ml-1 text-[11px] font-medium text-gray-400 dark:text-gray-500">
                                                / {goal} g
                                            </span>
                                        </span>
                                    </div>
                                    <div className="h-[6px] w-full rounded-full" style={{ backgroundColor: row.track }}>
                                        <div
                                            className="h-full rounded-full transition-all"
                                            style={{ width: `${pct}%`, backgroundColor: row.color }}
                                        />
                                    </div>
                                    <div className="mt-1.5 text-[11px] font-medium text-gray-400 dark:text-gray-500">
                                        {(macros[row.key] || 0).toFixed(1)} g over {totals.days} days
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="py-6 text-center text-[13px] text-gray-500 dark:text-gray-400">
                        No macronutrients estimated yet — they are calculated when a care note
                        recording food or drink is submitted.
                    </div>
                )}
            </div>

            {/* Chart Section */}
            <div className="bg-white dark:bg-[#262b33] border border-gray-50 dark:border-gray-800 rounded-[24px] shadow-sm p-8 pb-10 mb-8">
                {nutrientsType === 'Fluid' ? <FluidChart /> : <FoodChart />}
            </div>

            {/* Records Section */}
            <div>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[17px] font-bold text-gray-800 dark:text-gray-100 italic">Recent {nutrientsType.toLowerCase()} records</h3>
                    <button className="text-[13px] font-bold text-blue-600 dark:text-blue-400 hover:underline">See all</button>
                </div>

                {/* Table Header */}
                <div className="grid grid-cols-6 px-8 py-3 text-[12px] font-bold text-[#6B7280] dark:text-gray-400 uppercase tracking-wider">
                    <div>Staff name</div>
                    <div>Date</div>
                    <div>Time</div>
                    <div>Item</div>
                    {nutrientsType === 'Fluid' ? (
                        <>
                            <div>Fluid in</div>
                            <div>Fluid out</div>
                        </>
                    ) : (
                        <>
                            <div>Portion</div>
                            <div>Calories</div>
                        </>
                    )}
                </div>

                {/* Table Rows */}
                <div className="flex flex-col gap-3">
                    {currentRecords.length > 0 ? (
                        currentRecords.map((row) => (
                            <div
                                key={row.id}
                                className="grid grid-cols-6 items-center px-8 py-5 bg-white dark:bg-[#262b33] rounded-[16px] shadow-sm border border-gray-50 dark:border-gray-800"
                            >
                                <div className="text-[14px] font-semibold text-gray-800 dark:text-gray-200">{row.staff}</div>
                                <div className="text-[14px] font-semibold text-gray-500 dark:text-gray-400">{row.date}</div>
                                <div className="text-[14px] font-semibold text-gray-500 dark:text-gray-400">{row.time}</div>
                                <div>
                                    <div className="text-[14px] font-semibold text-gray-800 dark:text-gray-200">{row.item}</div>
                                    {/* The meal slot the carer logged it under (Lunch, Drinking…) */}
                                    {row.category && row.category !== row.item && (
                                        <div className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{row.category}</div>
                                    )}
                                </div>
                                {nutrientsType === 'Fluid' ? (
                                    <>
                                        {/* An amount of 0 means the carer logged the drink without a
                                            volume — say so rather than reporting "0ml" as if none
                                            was given. */}
                                        {mlOf(row) > 0 ? (
                                            <div className="text-[14px] font-semibold text-green-600 dark:text-green-400">{row.in}</div>
                                        ) : (
                                            <div className="text-[13px] font-medium italic text-gray-400 dark:text-gray-500">Not recorded</div>
                                        )}
                                        {/* Fluid out is recorded separately, not in care notes */}
                                        <div className="text-[14px] font-semibold text-gray-400 dark:text-gray-500">{row.out ?? '—'}</div>
                                    </>
                                ) : (
                                    <>
                                        <div className="text-[14px] font-semibold text-gray-500 dark:text-gray-400">{row.portion}</div>
                                        {row.calories > 0 ? (
                                            <div className="text-[14px] font-semibold text-orange-600 dark:text-orange-400 flex items-center gap-1">
                                                {row.calories} <span className="text-[10px] font-bold uppercase">kcal</span>
                                            </div>
                                        ) : (
                                            <div className="text-[13px] font-medium italic text-gray-400 dark:text-gray-500">Not estimated</div>
                                        )}
                                    </>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="py-10 text-center text-gray-500 dark:text-gray-400 bg-white dark:bg-[#262b33] rounded-[16px] border border-dashed border-gray-200 dark:border-gray-800">
                            No {nutrientsType.toLowerCase()} records found for this period.
                        </div>
                    )}
                </div>
            </div>

            {/* Goal editor — writes the resident's target, the same one the
                mobile app reads and sets. */}
            <Modal
                isOpen={!!goalEditor}
                onClose={() => setGoalEditor(null)}
                title={goalEditor ? `Daily ${GOAL_KINDS[goalEditor].label} goal` : ''}
                footer={
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={() => setGoalEditor(null)}
                            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#31373f] transition"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={saveGoal}
                            disabled={savingGoal}
                            className="px-5 py-2 rounded-lg text-sm font-medium text-white bg-[#2563EB] hover:bg-blue-700 disabled:bg-[#93C5FD] transition"
                        >
                            {savingGoal ? 'Saving…' : 'Save goal'}
                        </button>
                    </div>
                }
            >
                {goalEditor && (
                    <div>
                        <p className="text-[13px] text-gray-500 dark:text-gray-400 mb-4">
                            Intake is measured against this target on the dashboard and in the
                            mobile app.
                        </p>

                        <div className="relative">
                            <input
                                type="number"
                                min="1"
                                max={GOAL_KINDS[goalEditor].max}
                                value={goalValue}
                                onChange={(e) => { setGoalValue(e.target.value); setGoalError(null); }}
                                className="w-full px-4 py-2.5 pr-14 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#31373f] text-[14px] text-gray-800 dark:text-gray-100 focus:outline-none focus:border-[#2563EB]"
                            />
                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[12px] font-medium text-gray-400">
                                {GOAL_KINDS[goalEditor].unit}
                            </span>
                        </div>

                        {goalError && (
                            <p className="mt-2 text-[12px] font-medium text-red-600 dark:text-red-400">{goalError}</p>
                        )}

                        <div className="flex flex-wrap gap-2 mt-4">
                            {GOAL_KINDS[goalEditor].presets.map((preset) => (
                                <button
                                    key={preset}
                                    onClick={() => { setGoalValue(String(preset)); setGoalError(null); }}
                                    className="px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-700 text-[12px] font-medium text-gray-600 dark:text-gray-300 hover:border-[#2563EB] hover:text-[#2563EB] transition"
                                >
                                    {preset} {GOAL_KINDS[goalEditor].unit}
                                </button>
                            ))}
                        </div>

                        {goalEditor === 'calories' && (
                            <p className="mt-4 text-[12px] text-gray-400 dark:text-gray-500">
                                The carb / fat / protein targets are split from this goal
                                automatically.
                            </p>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default NutrientsTab;
